import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useRolesEfectivos } from "@/lib/sesion";

/**
 * Roles internos del OC: tienen acceso al módulo Telecom siempre, sin importar
 * sus contextos de actuación (no actúan como tramitadores). Lista canónica en
 * backend: {@code IamApi.ROLES_INTERNOS_NPMX} — mantener sincronizada.
 */
const ROLES_INTERNOS = new Set([
  "ADMIN_SICEN",
  "GERENTE_CALIDAD",
  "AUXILIAR_CALIDAD",
  "RESPONSABLE_TELECOM",
  "RESPONSABLE_ELECTRICA",
  "RESPONSABLE_EFICIENCIA",
  "RESPONSABLE_CERTIFICADOS",
  "EVALUADOR_TECNICO",
  "COORDINADOR_TECNICO",
  "AUDITOR_INTERNO",
  "DIRECCION",
  "VIGILANCIA",
  "FACTURACION",
]);


export interface Contexto {
  asociacionId: string | null;
  titularId: string;
  titularRazonSocial: string;
  tramitadorId: string | null;
  tramitadorRazonSocial: string | null;
  tipoAutorizacion: string;
  sectoresCodigos: string[];
  titularPuedeActuarComoTitular: boolean;
  titularPuedeActuarComoTramitador: boolean;
}

/**
 * Devuelve el contexto activo del usuario (titular seleccionado en
 * sessionStorage), o el primero si no hay selección. Los internos del OC no
 * tienen contextos; devuelve null en ese caso.
 */
export function useContextoActivo(): { contexto: Contexto | null; loading: boolean; esInterno: boolean } {
  const roles = useRolesEfectivos();
  const esInterno = roles.some((r) => ROLES_INTERNOS.has(r));

  const q = useQuery<Contexto[]>({
    queryKey: ["contextos-mios"],
    queryFn: async () => (await api.get<Contexto[]>("/api/contextos/mios")).data,
    enabled: !esInterno,
    staleTime: 30_000,
  });

  if (esInterno) return { contexto: null, loading: false, esInterno: true };
  if (q.isLoading) return { contexto: null, loading: true, esInterno: false };
  const titularId = window.sessionStorage.getItem("sicen.contexto.titularId");
  const activo = (q.data ?? []).find((c) => c.titularId === titularId) ?? q.data?.[0] ?? null;
  return { contexto: activo, loading: false, esInterno: false };
}

interface AccessResult {
  allowed: boolean;
  loading: boolean;
  esInterno: boolean;
  /** Razón social del titular del contexto activo (si hay). */
  titularRazonSocial: string | null;
  /** True si no hay contexto seleccionado / disponible para el cliente. */
  sinContexto: boolean;
}

/**
 * Determina si el usuario actual puede entrar al módulo Telecom:
 *  - Internos del OC: siempre sí.
 *  - Clientes: solo si el contexto activo (en sessionStorage) tiene el sector
 *    "TELECOM" en sus `sectoresCodigos`.
 *
 * Reutiliza el query `["contextos-mios"]` que ya cachea el ContextSelector.
 */
export function useTelecomAccess(): AccessResult {
  const roles = useRolesEfectivos();
  const esInterno = roles.some((r) => ROLES_INTERNOS.has(r));

  const q = useQuery<Contexto[]>({
    queryKey: ["contextos-mios"],
    queryFn: async () => (await api.get<Contexto[]>("/api/contextos/mios")).data,
    enabled: !esInterno,
    staleTime: 30_000,
  });

  if (esInterno) {
    return { allowed: true, loading: false, esInterno: true, titularRazonSocial: null, sinContexto: false };
  }
  if (q.isLoading) {
    return { allowed: false, loading: true, esInterno: false, titularRazonSocial: null, sinContexto: false };
  }

  const titularId = window.sessionStorage.getItem("sicen.contexto.titularId");
  const activo = (q.data ?? []).find((c) => c.titularId === titularId) ?? q.data?.[0] ?? null;
  const allowed = !!activo?.sectoresCodigos?.includes("TELECOM");

  return {
    allowed,
    loading: false,
    esInterno: false,
    titularRazonSocial: activo?.titularRazonSocial ?? null,
    sinContexto: !activo,
  };
}
